import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { SessionService } from '../modules/auth/services/session.service';

/**
 * Token Refresh Interceptor (web / HttpOnly cookie)
 *
 * - Na 401 próbuje odświeżyć sesję przez /auth/refresh (cookie).
 * - Równoległe żądania, które dostały 401 w trakcie refresha, są kolejkowane
 *   i ponowione po sukcesie.
 * - Jeśli refresh nie wychodzi (401/403) → clearSession + toast + redirect /auth/login.
 * - 5xx/network → sesja zostaje, error przechodzi dalej, user może ponowić.
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private static readonly MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

  private isRefreshing = false;
  private readonly refreshCompleted$ = new BehaviorSubject<boolean | null>(null);

  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translocoService = inject(TranslocoService);
  private readonly sessionService = inject(SessionService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isPublicAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isTokenRefreshEndpoint(request.url)) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshCompleted$.next(null);

      return this.sessionService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshCompleted$.next(true);
          return next.handle(this.withFreshCsrf(request));
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshCompleted$.next(false);

          // Wylogowuj TYLKO przy auth errors (401/403 na /auth/refresh oznacza brak ważnego refresh cookie).
          // 5xx, 0 (network), inne 4xx — zachowaj sesję, user może ponowić akcję.
          const status = error?.status;
          if (status === 401 || status === 403) {
            this.handleLogout();
          }

          return throwError(() => error);
        }),
      );
    }

    return this.refreshCompleted$.pipe(
      filter((result) => result !== null),
      take(1),
      switchMap((success) => {
        if (success) {
          return next.handle(this.withFreshCsrf(request));
        }
        return throwError(() => new Error('Session refresh failed'));
      }),
    );
  }

  private handleLogout(): void {
    this.sessionService.clearSession();

    const summary = this.translocoService.translate('errors.errorMessageTitle');
    const detail = this.translocoService.translate('errors.AUTH.SESSION_EXPIRED');
    this.messageService.add({ severity: 'warn', summary, detail, life: 5000 });

    this.router.navigate(['/auth/login']);
  }

  /**
   * Po refreshu backend mógł zrotować `csrf_token` cookie. Dla mutacji nadpisz
   * header `X-CSRF-Token` aktualną wartością z cookie, żeby backend nie odrzucił
   * retry'owanego requestu.
   */
  private withFreshCsrf(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!TokenRefreshInterceptor.MUTATION_METHODS.has(request.method.toUpperCase())) {
      return request;
    }
    const csrf = this.getCookie('csrf_token');
    if (!csrf) {
      return request;
    }
    return request.clone({ setHeaders: { 'X-CSRF-Token': csrf } });
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  private isTokenRefreshEndpoint(url: string): boolean {
    return url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/verify-mfa');
  }

  private isPublicAuthEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/auth/activate-account',
      '/auth/activate',
      '/auth/reset-password',
      '/auth/request-password-reset',
      '/auth/register',
      '/auth/guest-login',
      '/auth/resend-activation',
      '/auth/login',
      '/auth/refresh',
    ];
    return publicEndpoints.some((endpoint) => url.includes(endpoint));
  }
}
